import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import SignUp from './components/Auth/Signup';
import Login from './components/Auth/Login';
import PrivateRoute from './components/Auth/PrivateRoute';
import Chats from './components/Chats';
import { useAuth } from './contexts/AuthContext';
import { useDowntime } from './contexts/DowntimeContext';
import './App.css';
import './Styles/animations.css';
import Footer from './components/Partials/Footer';
import Pricing from './components/Pricing/Pricing';
import NotFound from './components/Auth/404';
import SitewideBanner from './components/Partials/SitewideBanner';
import PaymentConfirmation from './components/PaymentConfirmation';
import StudySpaces from './components/Space/Spaces Page/StudySpaces';
import ScreenSizeWarning from './components/Partials/ScreenSizeWarning';
import StudySpace from './components/Space/Space Page/StudySpace';
import StudentPage from './components/Student/page';
import './Styles/Layout/Buttons.css';
import DevToolbar from './components/Layout/DevToolbar';
import { SettingsPage } from './components/Settings/SettingsPage';
import { ConfirmationModal } from './components/UtilityModals/ConfirmationModal';
import { NewWindowModal } from './components/UtilityModals/NewWindowModal';
import { CodeDetectionModal } from './components/UtilityModals/CodeDetectionModal';
import { ModeModal } from './components/Mode/ModeModal';
import { useAttachmentContext } from './contexts/AttachmentContext';
import { useWindow } from './contexts/WindowContext';
import { WelcomeView } from './components/WelcomeView';
import { ClientOutOfDate } from './components/Toasts/ClientOutOfDate';
import { TextViewerModal } from './components/Partials/TextViewerModal';
import Sidebar from './components/Sidebar';
import { exchangeGoogleCode } from './services/api';
import { PRE_DRIVE_REDIRECT_CHAT_KEY } from './components/Layout/Input-Area/AttachButton';
import { GoogleDriveModal } from './components/Layout/Input-Area/Upload-Modals/GoogleDriveModal';
import LoadingOverlay from './components/Partials/LoadingOverlay';
import { AppProviders } from './providers/AppProviders';
import './Styles/Layout/ConvexErrorBoundary.css';
import './Styles/Visualizations.css';
import ImageModal from './components/Partials/ImageModal';
import { DowntimeView } from './components/Layout/Views/DowntimeView';
import SalesModal from './components/Partials/SalesModal';
import { useToast } from './contexts/ToastContext';
import { ResearchActivityPreview } from './components/Message/ResearchActivityPreview';
import { MessageRenderingPreview } from './components/Message/MessageRenderingPreview';
import { ImageGenerationPreview } from './components/Message/ImageGenerationPreview';
import SharedChat from './components/Share/SharedChat';

interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  
  return isAuthenticated ? (
    <Navigate to={location.state?.from?.pathname || "/"} replace />
  ) : (
    <>{children}</>
  );
};

interface ExternalRedirectProps {
  to: string;
  onMount?: () => void;
}

const ExternalRedirect: React.FC<ExternalRedirectProps> = ({ to, onMount }) => {
  useEffect(() => {
    if (onMount) onMount();
    window.location.href = to;
  }, [to, onMount]);
  return null;
};

const AppContent: React.FC = () => {
  const { isLoading, isAuthenticated, refetchInitialData, user } = useAuth();
  const { downtime } = useDowntime();
  const { codeDetectionState, closeCodeDetectionModal } = useWindow();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { handleGoogleDriveModalOpen } = useAttachmentContext();
  const [isChatsPage, setIsChatsPage] = useState(false);
  const hasShownOfflineToast = useRef(false);

  const isSpacesPage = location.pathname.startsWith('/spaces');
  const isSettingsPage = location.pathname.startsWith('/settings');
  const isSharedChatPage = location.pathname.startsWith('/share/');
  const showSidebar = isSpacesPage || isChatsPage || isSettingsPage || isSharedChatPage
  
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(segment => segment !== '');
    const isRoot = location.pathname === '/';
    const isChatIdPath = pathSegments.length === 1 && /^(?=.*[a-zA-Z])(?=.*[0-9])[a-zA-Z0-9-]+$/.test(pathSegments[0]);

    const isCurrentChatsPage = isRoot || isChatIdPath;
    setIsChatsPage(isCurrentChatsPage);

    if (!isCurrentChatsPage) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, isChatsPage]);

  useEffect(() => {
    const handleGoogleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');

      if (window.location.pathname === '/google-drive/callback' && (code || error) && isAuthenticated && user) {
        if (error) {
          console.error('Google OAuth error received:', error);
          navigate('/', { replace: true });
        } else if (code) {
          handleGoogleDriveModalOpen([], true);
          const preDriveRedirectChatId = localStorage.getItem(PRE_DRIVE_REDIRECT_CHAT_KEY);
          try {
            const response = await exchangeGoogleCode(code, window.location.origin + '/google-drive/callback');
            handleGoogleDriveModalOpen(response.files, false);

            refetchInitialData();

            if (preDriveRedirectChatId) {
              localStorage.removeItem(PRE_DRIVE_REDIRECT_CHAT_KEY);
              navigate(`/${preDriveRedirectChatId}`, { replace: true });
            } else {
              navigate('/', { replace: true });
            }
          } catch (error) {
            console.error('Callback API call error:', error);
            navigate('/', { replace: true });
          }
        }
      }
    };

    handleGoogleCallback();
  }, [location.pathname, location.search, refetchInitialData, navigate, isAuthenticated, user, handleGoogleDriveModalOpen]);

  useEffect(() => {
    const notifyOffline = () => {
      if (hasShownOfflineToast.current) return;
      toast('You are offline. Please check your network connection.', 'warning');
      hasShownOfflineToast.current = true;
    };

    const resetOfflineToast = () => {
      hasShownOfflineToast.current = false;
    };

    if (!navigator.onLine) {
      notifyOffline();
    }

    window.addEventListener('offline', notifyOffline);
    window.addEventListener('online', resetOfflineToast);

    return () => {
      window.removeEventListener('offline', notifyOffline);
      window.removeEventListener('online', resetOfflineToast);
    };
  }, [toast]);

  const isPricingPage = /^\/pricing/.test(location.pathname);
  const isStudentPage = /^\/(yorku|umanitoba|student)/.test(location.pathname);
  const isInfoPage = (isPricingPage || isStudentPage) && !downtime.isDowntime;

  return (
    <div className="app-container">
      <DevToolbar />
      {downtime.isDowntime ? (
        <DowntimeView />
      ) : (
        <main className="content">
            {/* Absolute Components */}
            <SitewideBanner />
            <ConfirmationModal />
            <NewWindowModal />
            <CodeDetectionModal 
              isOpen={codeDetectionState.isOpen}
              onClose={closeCodeDetectionModal}
              codeContent={codeDetectionState.codeContent}
              onPasteAnyway={codeDetectionState.onPasteAnyway}
            />
            <WelcomeView />
            <ModeModal />
            <ScreenSizeWarning />
            <ClientOutOfDate />
            <TextViewerModal />
            <GoogleDriveModal />
            <ImageModal />
            <SalesModal />

            
            <div className="content-column">
              {showSidebar && <Sidebar />}
              <div className="content-main-area">
              <Routes>
                <Route path="/" element={
                  <Chats />
                } />
                <Route 
                  path="/new" 
                  element={<Navigate to="/" replace />} 
                />
                <Route 
                  path="/undefined" 
                  element={<Navigate to="/" replace />} 
                />
                <Route 
                  path="/privacy-policy" 
                  element={<ExternalRedirect to="https://cognora.ca/privacy-policy" />} 
                />
                <Route 
                  path="/terms-of-service" 
                  element={<ExternalRedirect to="https://cognora.ca/terms-of-service" />} 
                />
                <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/pricing/:tierName" element={<Pricing />} />
                <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
                <Route path="/yorku" element={<StudentPage />} />
                <Route path="/umanitoba" element={<StudentPage />} />
                <Route path="/student" element={<StudentPage />} />
                <Route path="/google-drive/callback" element={<LoadingOverlay message="Connecting to Google Drive" />} />
                <Route path="/share/:shareId" element={<SharedChat />} />
                <Route path="/chats" element={<Chats chatsListView={true} />} />
                {process.env.NODE_ENV === 'development' && (
                  <Route path="/dev/research-activity" element={<ResearchActivityPreview />} />
                )}
                {process.env.NODE_ENV === 'development' && (
                  <Route path="/dev/message-rendering" element={<MessageRenderingPreview />} />
                )}
                {process.env.NODE_ENV === 'development' && (
                  <Route path="/dev/image-generation" element={<ImageGenerationPreview />} />
                )}
                <Route 
                  path="/spaces" 
                  element={
                    <PrivateRoute requiresElite={true}>
                      <StudySpaces />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/spaces/:id" 
                  element={
                    <PrivateRoute requiresElite={true}>
                      <StudySpace />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/settings" 
                  element={
                    <PrivateRoute>
                      <SettingsPage />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/settings/:section" 
                  element={
                    <PrivateRoute>
                      <SettingsPage />
                    </PrivateRoute>
                  } 
                />
                <Route
                  path="/:chatId?"
                  element={
                    isLoading ? (
                      <Chats />
                    ) :
                    isAuthenticated && location.pathname.slice(1) &&
                      /^(?=.*[a-zA-Z])(?=.*\d).+$/.test(location.pathname.slice(1)) ? (
                      <Chats />
                    ) : (
                      <NotFound />
                    )
                  }
                />
                
                <Route path='*' element={<NotFound />} />
              </Routes>
              {isInfoPage && <Footer />}
              </div>
            </div>
        </main>
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AppProviders>
      <AppContent />
    </AppProviders>
  );
};

export default App;
